/**
 * A short scene path for an object, e.g. `Car/Body`.
 *
 * A bare name does not locate anything: a savings row read "drawn by Object_6"
 * in a scene with three of them. A couple of ancestors turns the name into a
 * place. Kept short on purpose — the full path to a deep node is mostly noise,
 * and this text is paid for by the token in an AI report.
 *
 * Structural typing, no three import: anything with `name`/`parent` works, which
 * is also what makes it testable without a scene.
 */

interface PathNode {
    readonly name?: unknown;
    readonly type?: unknown;
    readonly parent?: unknown;
}

export function objectPathLabel(object: unknown, maxSegments = 3): string {
    if (!object || typeof object !== "object") return "";
    const segments: string[] = [];
    const seen = new Set<unknown>(); // a malformed host scene can have a parent cycle
    let node: unknown = object;
    let first = true;

    while (node && typeof node === "object" && segments.length < maxSegments && !seen.has(node)) {
        seen.add(node);
        const n = node as PathNode;
        const name = typeof n.name === "string" ? n.name.trim() : "";
        // the object ITSELF falls back to its type ("Mesh"); ancestors do not —
        // a chain of "Object3D/Group/Object3D" says less than nothing
        const segment = name || (first && typeof n.type === "string" ? n.type : "");
        if (segment) segments.unshift(segment);
        first = false;
        node = n.parent;
    }
    return segments.join("/");
}
